import { Checkbox, FaqComponent, Icon } from '@arcadiapower/shrike';
import { useEffect, useRef, useState } from 'react';
import { Button } from '~/components/button';
import { FaqButton } from '~/components/button/button.style';
import { Wrapper } from '~/components/customer-details/customer-details.style';
import { COMMUNITY_SOLAR } from '~/components/dialogs';
import { ErrorMessage } from '~/components/error-message';
import { ImageSelector, type ImageType, UTILITY_CONNECT_IMAGE } from '~/components/image';
import { TwoColumnGrid } from '~/components/layout';
import { Link } from '~/components/link';
import { Description, Title, Text, Ul, UlListItem } from '~/components/typography';
import { useCopy } from '~/hooks';
import { LDClient, LDFlags } from '~/providers';
import { FetchFunnelPacketResponse } from '~/services/fetch-funnel-packet';
import { UploadedImage, availableDocumentTypesPerUtility } from '~/utils/files';
import { store } from '~/utils/store';
import { LMIProofDocSkipConfirmationDialog } from '../../lmi-proof-docs-upload/lmi-proof-doc-skip-confirmation-dialog';
import { getFaqData } from '../faq.data';
import { DocumentDetails, Document } from './document-details.component';
import { LMIProofDocUploader } from './lmi-proof-doc-uploader.component';

export const LMIProofDocsUpload = ({
  context,
  next,
  skip,
  overrideCopy,
  flags,
  error,
  loading,
  pending,
}: LMIProofDocsUploadProps) => {
  const enableManualProofCapture = LDClient.variation(LDFlags.enableManualProofCapture);
  const copy = useCopy(context, defaultCopy, overrideCopy);
  const [failure, setFailure] = useState(false);
  const [docs, setDocs] = useState<LMIProofDoc[] | Document[]>([]);
  const [documentTypes, setDocumentTypes] = useState([] as string[]);
  const [showSkipConfirmationDialog, setShowSkipConfirmationDialog] = useState(false);
  const [documentTermsChecked, setDocumentTermsChecked] = useState(!enableManualProofCapture);
  const hasErrorDoc = docs.some((doc: LMIProofDoc | Document) => 'image' in doc && doc.image.error);

  const [showSkipThisStepLink, setShowSkipThisStepLink] = useState(
    LDClient.variation(LDFlags.allowUserToSkipLmiProof),
  );
  const [lmiProofDocOptionsAvailableInLD, setLmiProofDocOptionsAvailableInLD] = useState(
    LDClient.variation(LDFlags.fetchUtilityLmiProofDocOptions),
  );
  const [lmiProofDocTypeOptions, setLmiProofDocTypeOptions] = useState(
    LDClient.variation(LDFlags.ffUtilityLmiProofDocsLabelOptions),
  );

  const [shouldBeDisabled, setShouldBeDisabled] = useState(false);

  const faqRef = useRef<HTMLDivElement>(null);
  const backToTopRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    /**
     * if user reloads the page while in upload page, the component mounts
     * before the solar machine initialize and identify() with Launch Darkly.
     * to prevent the component from using stale data, we add a listener to
     * update the needed LD values 'onChange' below.
     */
    LDClient.on('change', () => {
      setShowSkipThisStepLink(LDClient.variation(LDFlags.allowUserToSkipLmiProof));
      if (context.shouldEnableSkip !== undefined) {
        setShowSkipThisStepLink(context.shouldEnableSkip); // For cases where we reload the page
      }
    });
  }, []);

  useEffect(() => {
    /**
     *  Same as above, needed for correct proof docs to be displayed
     *  when user reloads the page while in upload page, on change is accepting
     *  only 1 flag at a time, so we need to add a listener for each flag
     */
    LDClient.on('change', () => {
      setLmiProofDocOptionsAvailableInLD(
        LDClient.variation(LDFlags.fetchUtilityLmiProofDocOptions),
      );
      setLmiProofDocTypeOptions(LDClient.variation(LDFlags.ffUtilityLmiProofDocsLabelOptions));
    });
  }, []);

  useEffect(() => {
    setShowSkipThisStepLink(LDClient.variation(LDFlags.allowUserToSkipLmiProof));
    if (context.shouldEnableSkip !== undefined) {
      setShowSkipThisStepLink(context.shouldEnableSkip);
    }
  }, [context.shouldEnableSkip]);

  // eslint-disable-next-line camelcase
  const __EXPERIMENTAL_getDocumentTypes = (): string[] | Record<string, string> => {
    if (lmiProofDocTypeOptions && Object.keys(lmiProofDocTypeOptions).length > 0) {
      return lmiProofDocTypeOptions;
    } else {
      const localProofDocOptions =
        availableDocumentTypesPerUtility[context?.funnelPacket?.utility?.id] ||
        context.documentTypes;

      return lmiProofDocOptionsAvailableInLD || localProofDocOptions;
    }
  };

  const disableNextButton = () => {
    return enableManualProofCapture && !flags?.showFaqButton
      ? pending || failure || !documentTermsChecked || !shouldBeDisabled
      : pending || failure || docs.length === 0 || hasErrorDoc;
  };

  return (
    <Wrapper ref={backToTopRef} style={{ border: 'none' }}>
      <TwoColumnGrid testId="screen-LMIProofDocsUpload">
        <TwoColumnGrid.Primary>
          {flags && (
            <Text color="secondary" fontWeight="700" fontSize="16px">
              {copy.step}
            </Text>
          )}
          <Title mb={4}>{copy.title}</Title>
          <Description mb={6}>{copy.description}</Description>
          {enableManualProofCapture && !flags?.showFaqButton && (
            <>
              <Description mb={4}>{copy.info}</Description>
              <Ul mb={4}>
                {copy.infoList.map(text => (
                  <UlListItem key={text}>{text}</UlListItem>
                ))}
              </Ul>
              <Description mb={4}>{copy.missingDocumentInfo}</Description>
              <Description mb={4}>{copy.additionalDocumentInfo}</Description>
            </>
          )}
          {flags?.showFaqButton ? (
            <FaqButton
              fullWidth={false}
              mode="default"
              onClick={() => {
                faqRef?.current?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              <Icon
                color="primary"
                icon="InfoCircleInverted"
                margin="5px"
                opacity="high"
                path="M 1,3 A 2,2 0,0,1 5,3 A 2,2 0,0,1 9,3 Q 9,6 5,9 Q 1,6 1,3 z"
                scale={2}
                title="InfoCircleInverted"
              />
              View FAQs
            </FaqButton>
          ) : null}
          {!enableManualProofCapture || flags?.showFaqButton ? (
            <LMIProofDocUploader
              documentTypes={documentTypes}
              docs={docs as LMIProofDoc[]}
              pending={pending}
              allDocumentTypes={__EXPERIMENTAL_getDocumentTypes()}
              onDocsChange={setDocs}
              onDocumentTypesChange={setDocumentTypes}
              onFailureChange={setFailure}
            />
          ) : (
            <DocumentDetails
              allDocumentTypes={__EXPERIMENTAL_getDocumentTypes()}
              pending={pending}
              setShouldBeDisabled={setShouldBeDisabled}
              setDocs={setDocs}
            />
          )}
          {enableManualProofCapture && !flags?.showFaqButton && (
            <Checkbox
              margin="16px 0 16px 0"
              checked={documentTermsChecked}
              onChange={e => setDocumentTermsChecked(e.target.checked)}
            >
              <Text textStyle="paragraph400" color="secondary">
                {copy.documentTerms}
              </Text>
            </Checkbox>
          )}
          {error ? <ErrorMessage error={error} /> : null}
          <Button
            type="submit"
            mode="primary"
            disabled={disableNextButton()}
            loading={loading}
            mt={6}
            mb={6}
            onClick={() => {
              next(docs);
            }}
            testId="lmi-proof-docs-upload-next"
          >
            {copy.saveContinue}
          </Button>
          {showSkipThisStepLink && (
            <Link
              color="primary"
              type="standalone"
              alignSelf="center"
              onClick={async e => {
                e.stopPropagation();
                if (pending) return;
                if (docs.length) {
                  setShowSkipConfirmationDialog(true);
                }
                store.set('PROOF_DOC_SKIPPED', true);
                skip([]);
              }}
              openInNewTab={false}
              testId="lmi-proof-docs-upload-skip"
            >
              {copy.skip}
            </Link>
          )}
        </TwoColumnGrid.Primary>
        <TwoColumnGrid.Secondary>
          {flags?.showFaqButton ? (
            <Wrapper
              gridColumn="auto / span 2"
              display="flex"
              flexGrow={1}
              style={{ borderRadius: '0px' }}
            >
              <FaqComponent
                /** shrike need to properly export FaqData type */
                data={getFaqData(__EXPERIMENTAL_getDocumentTypes()) as any}
                header="Frequently Asked Questions"
              />
            </Wrapper>
          ) : (
            <TwoColumnGrid.Secondary>
              <ImageSelector type={copy.imageType} />
            </TwoColumnGrid.Secondary>
          )}
        </TwoColumnGrid.Secondary>
        {flags?.showFaqButton ? (
          <TwoColumnGrid.MobileOnlySecondary>
            <Wrapper
              ref={faqRef}
              gridColumn="auto / span 2"
              display="flex"
              flexGrow={1}
              style={{ borderRadius: '0px' }}
            >
              <FaqComponent
                /** shrike need to properly export FaqData type */
                data={getFaqData(__EXPERIMENTAL_getDocumentTypes()) as any}
                header="Frequently Asked Questions"
              />
            </Wrapper>
            <Wrapper
              onClick={() => {
                backToTopRef?.current?.scrollIntoView({ behavior: 'smooth' });
              }}
              style={{
                textDecoration: 'underline',
                textUnderlineOffset: ' 2px',
              }}
            >
              <Text textAlign="center" mt={5} paddingBottom={5} textStyle="heading600">
                Back to top
                <Icon
                  color="primary"
                  icon="ArrowUp"
                  margin="5px"
                  opacity="high"
                  path="M 1,3 A 2,2 0,0,1 5,3 A 2,2 0,0,1 9,3 Q 9,6 5,9 Q 1,6 1,3 z"
                  scale={2}
                  title="ArrowUp"
                />
              </Text>
            </Wrapper>
          </TwoColumnGrid.MobileOnlySecondary>
        ) : null}
      </TwoColumnGrid>
      {showSkipConfirmationDialog && (
        <LMIProofDocSkipConfirmationDialog
          onSkip={() => {
            setShowSkipConfirmationDialog(false);
            setDocs([]);
            skip([]);
          }}
          onCancel={() => setShowSkipConfirmationDialog(false)}
        />
      )}
    </Wrapper>
  );
};

/*
This copy is used accross all the funnels
*/

export const defaultCopy = () => ({
  description:
    'Participation in select assistance programs can help you get enrolled on a solar farm faster and/or see additional community solar savings. Please upload a photo of a card, letter, utility bill, or screenshot from your program portal that proves your active participation in one of the qualifying programs (supported formats: PNG, JPEG, HEIC).',
  info: 'Your document must include the following information:',
  missingDocumentInfo:
    'If your document is missing any of this information, please upload an additional document that includes the missing information.',
  infoList: ['Qualifying program name', 'Participant name', 'Issue date or expiration date'],
  additionalDocumentInfo:
    'If you upload an additional document, ensure that either the participant name or an ID number are present on both documents.',
  documentTerms:
    'By checking this box, I hereby affirm that the documentation I have provided is true and accurate to the best of my knowledge, is currently effective and/or has been effective within the past twelve (12) months, and corresponds with my own enrollment and participation, or the enrollment and participation of another member of my household, in the income-qualifying assistance program identified therein.',
  learnMoreModal: COMMUNITY_SOLAR,
  imageType: UTILITY_CONNECT_IMAGE as ImageType,
  saveContinue: 'Save and continue',
  skip: 'Skip this step',
  title: 'Add your income eligibility documents',
  step: 'Step 3 of 4',
});

export type LmiProofDocsUploadCopy = ReturnType<typeof defaultCopy>;

export interface LMIProofDocsUploadProps {
  context: LMIProofDocsUploadContext;
  flags?: {
    showFaqButton: boolean;
  };
  next: (docs) => void;
  skip: (docs) => void;
  overrideCopy?: (context: LMIProofDocsUploadContext) => Partial<LmiProofDocsUploadCopy>;
  error?: Error | null;
  loading: boolean;
  pending: boolean;
}

export interface LMIProofDoc {
  documentType: string;
  image: UploadedImage;
}

export interface LMIProofDocsUploadContext {
  funnelPacket?: any;
  docs: LMIProofDoc[] | Document[];
  documentTypes: string[];
  shouldEnableSkip?: boolean;
}

export interface UploadContext {
  funnelPacket: FetchFunnelPacketResponse;
  documentTypes: string[];
  docs: LMIProofDoc[] | Document[];
  shouldEnableSkip?: boolean;
  promoCode?: string;
  utmParams?: Record<string, string | number>;
}
